/**
 * Hosted WebRTC netplay — P1 streams canvas + audio to P2,
 * P2 sends controller input back via DataChannel.
 */

import { encodeInputFrame, encodeInputFrameEx, decodeInputFrame } from './netplay/input-parity.js';
import { onAudioContextCreated } from './audio-unlock.js';

// DataChannel ping/pong protocol — measures true P2P RTT (bypasses the
// server relay path that WebSocket pings traverse). 9-byte binary format
// avoids collision with input messages (2/4/6/8/10 bytes).
const DC_PING = 0x01;
const DC_PONG = 0x02;
const DC_PING_SIZE = 9; // 1 byte tag + 8 bytes Float64 timestamp
const DC_PING_INTERVAL_MS = 1000;

const RTC_CONFIG = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ],
};

export class HostedNetplay {
  /**
   * @param {import('./network.js').Network} network
   * @param {{ fps: number }} opts
   */
  constructor(network, opts = {}) {
    this.network = network;
    this.fps = opts.fps || 60;
    // In multi-peer rooms (N64 4-player) a guest must target the host explicitly
    // (toPlayer 0) instead of the untagged broadcast-relay used for 1v1. null =
    // legacy relay (unchanged 2-player behavior).
    this._signalTarget = (typeof opts.signalToPlayer === 'number') ? opts.signalToPlayer : null;
    this.pc = null;
    this.dataChannel = null;
    this.audioStreamDest = null;
    this._unhookAudioCreate = null;
    this._shadowedContexts = new Map();
    this._isHost = false;

    // Callbacks
    this.onRemoteInput = null;          // host: (bitmask) => {}
    this.onConnectionStateChange = null; // (state) => {}
    this.onError = null;                 // (msg) => {}
    this.onP2PRtt = null;               // (ms) => {} — P2P DataChannel RTT

    // P2P latency (null until first DC pong arrives)
    this.p2pLatency = null;
    this._dcPingInterval = null;

    // Voice chat state
    this._micStream = null;
    this._micTrack = null;
    this._voiceAudio = null;       // Audio element for remote voice
    this._gameStream = null;       // first stream received = game, used to distinguish voice
    this._micAnalyserCtx = null;
    this._micAnalyser = null;
    this._micAnalyserData = null;
    this._remoteAnalyserCtx = null;
    this._remoteAnalyser = null;
    this._remoteAnalyserData = null;

    // Guest-side Web Audio pipeline for game audio (bypasses iOS silent switch)
    this._guestAudioCtx = null;
    this._guestAudioSource = null;
    this._guestAudioAnchor = null; // muted HTMLAudioElement that keeps Chrome's decode pipeline alive

    // Buffer for signals that arrive before PC is ready
    this._pendingSignals = [];
    this._ready = false;

    // Wire signaling from network
    this._prevOnSignal = network.onSignal;
    network.onSignal = (payload) => {
      if (this._ready) {
        this.handleSignal(payload);
      } else {
        console.log('[hosted-netplay] Buffering signal:', payload?.type);
        this._pendingSignals.push(payload);
      }
    };
  }

  _flushPendingSignals() {
    this._ready = true;
    for (const sig of this._pendingSignals) {
      this.handleSignal(sig);
    }
    this._pendingSignals = [];
  }

  /** Send a WebRTC signal — targeted to the host in multi-peer rooms, else relayed. */
  _sendSignal(payload) {
    if (this._signalTarget != null) {
      this.network.sendSignalToPlayer(this._signalTarget, payload);
    } else {
      this.network.sendSignal(payload);
    }
  }

  // ── Audio Capture ──────────────────────────────────────

  /**
   * Register an AudioContext creation hook that shadows ctx.destination
   * with a tap node. Must be called BEFORE the emulator core starts so
   * the hook fires when the core's AudioContext is constructed.
   */
  prepareAudioCapture() {
    this._unhookAudioCreate = onAudioContextCreated((ctx) => {
      const realDest = ctx.destination;
      const tap = ctx.createGain();
      tap.gain.value = 1;

      this._muteGain = ctx.createGain();
      tap.connect(this._muteGain);
      this._muteGain.connect(realDest);

      this.audioStreamDest = ctx.createMediaStreamDestination();
      tap.connect(this.audioStreamDest);

      const originalDesc = Object.getOwnPropertyDescriptor(ctx, 'destination');
      Object.defineProperty(ctx, 'destination', {
        get: () => tap,
        configurable: true,
      });
      this._shadowedContexts.set(ctx, originalDesc);
    });
  }

  /** Mute/unmute the host's local audio without affecting the WebRTC stream. */
  setLocalMuted(muted) {
    if (this._muteGain) this._muteGain.gain.value = muted ? 0 : 1;
  }

  _cleanupAudioCapture() {
    if (this._unhookAudioCreate) {
      this._unhookAudioCreate();
      this._unhookAudioCreate = null;
    }
    for (const [ctx, originalDesc] of this._shadowedContexts) {
      if (originalDesc) {
        Object.defineProperty(ctx, 'destination', originalDesc);
      } else {
        delete ctx.destination;
      }
    }
    this._shadowedContexts.clear();
  }

  // ── Host ───────────────────────────────────────────────

  async startHost(canvas) {
    this._isHost = true;
    this.pc = new RTCPeerConnection(RTC_CONFIG);
    this._setupConnectionMonitoring();

    // Capture canvas video
    const videoStream = canvas.captureStream();
    for (const track of videoStream.getVideoTracks()) {
      this.pc.addTrack(track, videoStream);
    }

    // Add captured audio track to the same stream so the guest receives
    // a single MediaStream with both video and audio tracks.
    if (this.audioStreamDest) {
      const audioTrack = this.audioStreamDest.stream.getAudioTracks()[0];
      if (audioTrack) {
        this.pc.addTrack(audioTrack, videoStream);
      }
    }

    // Set video encoding parameters
    for (const sender of this.pc.getSenders()) {
      if (sender.track?.kind === 'video') {
        try {
          const params = sender.getParameters();
          if (!params.encodings?.length) params.encodings = [{}];
          params.encodings[0].maxBitrate = 2_500_000;
          params.encodings[0].maxFramerate = Math.ceil(this.fps);
          await sender.setParameters(params);
        } catch { /* encoding params not supported */ }
      }
    }

    // Create data channel for P2 input + P2P latency probes (unreliable, low latency)
    this.dataChannel = this.pc.createDataChannel('input', {
      ordered: false,
      maxRetransmits: 0,
    });
    this.dataChannel.binaryType = 'arraybuffer';
    this.dataChannel.onmessage = (e) => this._handleDCMessage(e);
    this.dataChannel.onopen = () => this._startDCPingLoop();

    // Receive remote voice track from guest
    this.pc.ontrack = (e) => {
      if (e.track.kind === 'audio' && e.streams[0]) {
        console.log('[hosted-netplay] Host received voice track from guest');
        this._receiveOwnedVoice(e.track, e.streams[0]);
      }
    };

    // ICE candidates
    this.pc.onicecandidate = (e) => {
      if (e.candidate) {
        this._sendSignal({ type: 'ice', candidate: e.candidate });
      }
    };

    // Mark ready and flush any buffered signals (e.g. ICE from guest)
    this._flushPendingSignals();

    // Create and send offer
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    console.log('[hosted-netplay] Host sending offer');
    this._sendSignal({ type: 'offer', sdp: offer.sdp });

    // Enable renegotiation handler AFTER initial offer (for mid-session voice chat)
    this._setupNegotiationNeeded();
  }

  /**
   * Re-establish hosting after a guest disconnect/rejoin.
   * Tears down the dead RTCPeerConnection + DataChannel but preserves
   * audioStreamDest (still receiving audio) and network.onSignal wiring.
   */
  async rehost(canvas) {
    this._stopDCPingLoop();
    this.p2pLatency = null;

    if (this.dataChannel) {
      this.dataChannel.close();
      this.dataChannel = null;
    }

    // Stop mic — user must re-enable on the new connection
    this._resetMicrophone();
    if (this._micAnalyserCtx) {
      this._micAnalyserCtx.close().catch(() => {});
      this._micAnalyserCtx = null;
    }
    this._micAnalyser = null;
    this._micAnalyserData = null;
    if (this._voiceAudio) {
      this._voiceAudio.srcObject = null;
      this._voiceAudio = null;
    }

    if (this.pc) {
      this.pc.close();
      this.pc = null;
    }

    // Reset signal buffering for the new peer
    this._ready = false;
    this._pendingSignals = [];

    await this.startHost(canvas);
  }

  // ── Guest ──────────────────────────────────────────────

  async startGuest(videoElement) {
    this._isHost = false;
    this.pc = new RTCPeerConnection(RTC_CONFIG);
    this._setupConnectionMonitoring();

    // Receive media stream — distinguish game tracks from voice tracks
    this.pc.ontrack = (e) => {
      console.log('[hosted-netplay] Guest received track:', e.track.kind, e.streams.length, 'streams');
      const stream = e.streams[0];
      if (!stream) return;

      if (!this._gameStream) {
        // First stream is the game (video + game audio)
        this._gameStream = stream;

        // Video element renders video. On desktop it will also play the
        // audio once the user clicks the overlay to unmute. On iOS the
        // hardware silent switch would mute it, so the overlay click
        // also builds a Web Audio graph that bypasses the switch.
        videoElement.srcObject = stream;
        videoElement.muted = true;
        videoElement.play().catch(() => {});

        // Show the tap overlay unconditionally — gives us a trusted
        // user gesture for both AudioContext creation and unmuting.
        this._showUnmuteOverlay(videoElement);
      } else if (stream !== this._gameStream && e.track.kind === 'audio') {
        // Subsequent audio-only stream = remote voice
        this._receiveOwnedVoice(e.track, stream);
      }
    };

    // Receive data channel
    this.pc.ondatachannel = (e) => {
      this.dataChannel = e.channel;
      this.dataChannel.binaryType = 'arraybuffer';
      this.dataChannel.onmessage = (ev) => this._handleDCMessage(ev);
      this.dataChannel.onopen = () => this._startDCPingLoop();
    };

    // ICE candidates
    this.pc.onicecandidate = (e) => {
      if (e.candidate) {
        this._sendSignal({ type: 'ice', candidate: e.candidate });
      }
    };

    // Mark ready and flush any buffered signals (offer may have arrived already)
    this._flushPendingSignals();

    // Enable renegotiation handler for mid-session voice chat
    this._setupNegotiationNeeded();
    console.log('[hosted-netplay] Guest ready, waiting for offer');
  }

  /**
   * Send controller input to the host (called by guest each frame).
   *
   * Analog input uses the 10-byte extended format (targetFrame + uint32
   * bitmask + signed-byte X/Y), including before a host-frame estimate exists.
   * Digital-only input with an integer `targetFrame` uses the 8-byte
   * parity-aware format. If null/undefined, it falls back to the legacy
   * 4-byte bare bitmask — used when the guest has
   * no host-frame estimate yet (e.g. before the first pong arrives
   * with frame piggyback, or when the host is on an older bundle).
   * Both formats are handled transparently by the host's decoder.
   */
  sendInput(bitmask, targetFrame, analogX, analogY) {
    if (this.dataChannel?.readyState !== 'open') return;
    if (Number.isFinite(analogX) || Number.isFinite(analogY)) {
      this.dataChannel.send(encodeInputFrameEx(targetFrame, bitmask, analogX, analogY));
    } else if (Number.isInteger(targetFrame)) {
      this.dataChannel.send(encodeInputFrame(targetFrame, bitmask));
    } else {
      const buf = new ArrayBuffer(4);
      new DataView(buf).setUint32(0, bitmask >>> 0, true);
      this.dataChannel.send(buf);
    }
  }

  // ── Signaling ──────────────────────────────────────────

  async handleSignal(payload) {
    if (!payload) return;
    if (!this.pc) {
      console.warn('[hosted-netplay] Signal received but no PeerConnection:', payload.type);
      return;
    }
    console.log('[hosted-netplay] handleSignal:', payload.type);
    try {
      switch (payload.type) {
        case 'offer':
          await this.pc.setRemoteDescription({ type: 'offer', sdp: payload.sdp });
          const answer = await this.pc.createAnswer();
          await this.pc.setLocalDescription(answer);
          console.log('[hosted-netplay] Sending answer (renegotiation)');
          this._sendSignal({ type: 'answer', sdp: answer.sdp });
          break;
        case 'answer':
          await this.pc.setRemoteDescription({ type: 'answer', sdp: payload.sdp });
          console.log('[hosted-netplay] Received answer');
          break;
        case 'ice':
          if (payload.candidate) {
            await this.pc.addIceCandidate(payload.candidate);
          }
          break;
      }
    } catch (e) {
      console.warn('[hosted-netplay] Signal handling error:', e);
    }
  }

  /**
   * Build the Web Audio graph for game audio. MUST be called from inside
   * a trusted user gesture (click/touch) — creating the AudioContext
   * inside the gesture sidesteps Chrome's "suspended" starting state,
   * and lets iOS route audio through the media channel (bypassing the
   * hardware silent switch).
   *
   * Pattern: we attach the audio-only MediaStream to a muted
   * HTMLAudioElement as an "anchor". Chrome won't pump audio through
   * a MediaStreamAudioSourceNode unless the underlying stream is also
   * attached to a media element — muting the anchor stops it from
   * producing its own output without stopping the decode pipeline.
   */
  async _enableGuestGameAudio() {
    if (this._guestAudioCtx) return; // already enabled
    const audioTrack = this._gameStream?.getAudioTracks?.()[0];
    if (!audioTrack) return;

    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;

    const ctx = new Ctx();
    this._guestAudioCtx = ctx;

    const audioOnly = new MediaStream([audioTrack]);

    // Muted anchor keeps Chrome's audio decode pipeline alive so the
    // Web Audio tap below actually receives samples.
    const anchor = new Audio();
    anchor.srcObject = audioOnly;
    anchor.muted = true;
    anchor.play().catch(() => {});
    this._guestAudioAnchor = anchor;

    const source = ctx.createMediaStreamSource(audioOnly);
    source.connect(ctx.destination);
    this._guestAudioSource = source;

    // Context was created inside a gesture, but it may still start
    // suspended on some browsers — resume explicitly.
    try { await ctx.resume(); } catch { /* ignore */ }
  }

  _showUnmuteOverlay(videoElement) {
    document.getElementById('guest-unmute-overlay')?.remove();
    const overlay = document.createElement('div');
    overlay.id = 'guest-unmute-overlay';
    overlay.style.cssText = `
      position:absolute; top:50%; left:50%; transform:translate(-50%,-50%);
      padding:16px 40px; cursor:pointer; z-index:10000;
      font-family:var(--font-pixel); font-size:14px; color:#00e5a0;
      letter-spacing:2px; text-transform:uppercase;
      background:rgba(0,0,0,0.85); border:1px solid rgba(0,229,160,0.3);
      border-radius:6px; transition:opacity 0.3s;
      min-width:200px; min-height:48px;
      display:flex; align-items:center; justify-content:center;
      -webkit-tap-highlight-color:transparent;
    `;
    overlay.textContent = 'Tap to Start Audio';
    videoElement.parentElement.style.position = 'relative';
    videoElement.parentElement.appendChild(overlay);
    overlay.addEventListener('click', async () => {
      try {
        await this._enableGuestGameAudio();
        overlay.remove();
      } catch (e) {
        console.warn('[hosted-netplay] Web Audio setup failed, retry available:', e);
        this._teardownGuestAudio();
        overlay.textContent = 'Tap to Retry Audio';
        return;
      }
      videoElement.muted = false;
      videoElement.play().catch(() => {});
    });
  }

  _teardownGuestAudio() {
    if (this._guestAudioSource) {
      try { this._guestAudioSource.disconnect(); } catch { /* ignore */ }
      this._guestAudioSource = null;
    }
    if (this._guestAudioCtx) {
      this._guestAudioCtx.close().catch(() => {});
      this._guestAudioCtx = null;
    }
    if (this._guestAudioAnchor) {
      try { this._guestAudioAnchor.pause(); } catch { /* ignore */ }
      this._guestAudioAnchor.srcObject = null;
      this._guestAudioAnchor = null;
    }
  }

  // ── Voice Chat ─────────────────────────────────────────

  /**
   * Request mic access and add the track to the peer connection.
   * Starts muted — call toggleVoiceMute() to unmute.
   * Triggers renegotiation so the remote side receives the track.
   */
  async enableVoice() {
    if (this._micTrack) return; // already enabled
    if (!this.pc) throw new Error('No peer connection');

    if (this._micPending) return this._micPending;
    const pending = this._enableOwnedVoice();
    this._micPending = pending;
    try { return await pending; }
    finally {
      if (this._micPending === pending) this._micPending = null;
    }
  }

  async _enableOwnedVoice() {
    const pc = this.pc;
    const owner = await this._reserveVoice();
    try {
      const stream = await owner.open();
      if (this.pc !== pc || this._microphoneOwner !== owner || owner.controller.signal.aborted) {
        owner.close();
        throw new Error('microphone_interrupted');
      }
      this._micStream = stream;
      this._micTrack = stream.getAudioTracks()[0];
      this._micTrack.enabled = false; // start muted
      pc.addTrack(this._micTrack, stream);
    } catch (error) {
      owner.releaseCapture();
      if (this._microphoneOwner === owner) { this._micTrack = null; this._micStream = null; }
      throw error;
    }
    console.log('[hosted-netplay] Mic track added to peer connection');

    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) {
        this._micAnalyserCtx = new Ctx();
        const source = this._micAnalyserCtx.createMediaStreamSource(this._micStream);
        this._micAnalyser = this._micAnalyserCtx.createAnalyser();
        this._micAnalyser.fftSize = 256;
        source.connect(this._micAnalyser);
        this._micAnalyserData = new Uint8Array(this._micAnalyser.fftSize);
      }
    } catch { /* metering is cosmetic */ }
  }

  /**
   * Toggle mic mute. Enables voice on first call if not yet enabled.
   * @returns {Promise<boolean>} true if now muted
   */
  async toggleVoiceMute() {
    if (!this._micTrack) {
      await this.enableVoice();
    }
    this._micTrack.enabled = !this._micTrack.enabled;
    console.log('[hosted-netplay] Mic', this._micTrack.enabled ? 'unmuted' : 'muted');
    return !this._micTrack.enabled;
  }

  /** @returns {boolean} true if mic is currently muted or not yet enabled */
  get voiceMuted() {
    return !this._micTrack || !this._micTrack.enabled;
  }

  getMicLevel() {
    if (!this._micAnalyser || !this._micTrack?.enabled) return 0;
    this._micAnalyser.getByteTimeDomainData(this._micAnalyserData);
    let sum = 0;
    for (let i = 0; i < this._micAnalyserData.length; i++) {
      const v = (this._micAnalyserData[i] - 128) / 128;
      sum += v * v;
    }
    return Math.min(1, Math.sqrt(sum / this._micAnalyserData.length) * 5);
  }

  getRemoteVoiceLevel() {
    if (!this._remoteAnalyser) return 0;
    this._remoteAnalyser.getByteTimeDomainData(this._remoteAnalyserData);
    let sum = 0;
    for (let i = 0; i < this._remoteAnalyserData.length; i++) {
      const v = (this._remoteAnalyserData[i] - 128) / 128;
      sum += v * v;
    }
    return Math.min(1, Math.sqrt(sum / this._remoteAnalyserData.length) * 5);
  }

  /** Reserve microphone ownership before capturing or receiving call audio. */
  async _reserveVoice() {
    const pc = this.pc;
    const { OwnedMicrophone } = await import('../shared/microphone-owner.js');
    if (!pc || this.pc !== pc) throw new Error('microphone_interrupted');
    if (this._microphoneOwner?.controller.signal.aborted) this._microphoneOwner = null;
    this._microphoneOwner ||= new OwnedMicrophone({ onEnded: () => { this._micTrack = null; this._micStream = null; } });
    const owner = this._microphoneOwner;
    try {
      await owner.reserve();
      if (this.pc !== pc || this._microphoneOwner !== owner || owner.controller.signal.aborted) {
        throw new Error('microphone_interrupted');
      }
      return owner;
    } catch (error) {
      owner.close();
      if (this._microphoneOwner === owner) this._microphoneOwner = null;
      throw error;
    }
  }

  _resetMicrophone() {
    // Closing invalidates pending permission requests as well as cached capture.
    const owner = this._microphoneOwner;
    this._microphoneOwner = null;
    this._micPending = null;
    if (owner) owner.close();
    else this._micTrack?.stop();
    this._micTrack = null;
    this._micStream = null;
  }

  _receiveOwnedVoice(track, stream) {
    const pc = this.pc;
    this._reserveVoice().then(owner => {
      if (this.pc === pc && !owner.controller.signal.aborted) this._playRemoteVoice(track, stream);
    }).catch(() => this.onError?.('Voice is unavailable while another call owns the microphone.'));
  }

  _playRemoteVoice(track, stream) {
    if (this._voiceAudio) {
      this._voiceAudio.srcObject = null;
    }
    if (this._remoteAnalyserCtx) {
      this._remoteAnalyserCtx.close().catch(() => {});
      this._remoteAnalyserCtx = null;
      this._remoteAnalyser = null;
      this._remoteAnalyserData = null;
    }

    this._voiceAudio = new Audio();
    this._voiceAudio.srcObject = stream;
    this._voiceAudio.play().catch(() => {});

    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) {
        this._remoteAnalyserCtx = new Ctx();
        const source = this._remoteAnalyserCtx.createMediaStreamSource(stream);
        this._remoteAnalyser = this._remoteAnalyserCtx.createAnalyser();
        this._remoteAnalyser.fftSize = 256;
        source.connect(this._remoteAnalyser);
        this._remoteAnalyserData = new Uint8Array(this._remoteAnalyser.fftSize);
      }
    } catch { /* metering is cosmetic */ }
  }

  /**
   * Handle renegotiation when tracks are added mid-session (e.g. voice).
   * Both sides can initiate — whichever adds a track first sends an offer,
   * and the existing handleSignal handles the answer symmetrically.
   */
  _setupNegotiationNeeded() {
    this._negotiating = false;
    this.pc.onnegotiationneeded = async () => {
      if (this._negotiating) return;
      this._negotiating = true;
      try {
        const offer = await this.pc.createOffer();
        await this.pc.setLocalDescription(offer);
        console.log('[hosted-netplay] Renegotiation: sending offer');
        this._sendSignal({ type: 'offer', sdp: offer.sdp });
      } catch (e) {
        console.warn('[hosted-netplay] Renegotiation failed:', e);
      } finally {
        this._negotiating = false;
      }
    };
  }

  // ── DataChannel P2P Latency ─────────────────────────────

  _handleDCMessage(e) {
    const data = e.data;
    if (!(data instanceof ArrayBuffer)) return;

    if (data.byteLength === DC_PING_SIZE) {
      const view = new DataView(data);
      const tag = view.getUint8(0);
      if (tag === DC_PING) {
        // Echo back as pong (same timestamp so sender can measure RTT)
        const pong = new ArrayBuffer(DC_PING_SIZE);
        const pv = new DataView(pong);
        pv.setUint8(0, DC_PONG);
        pv.setFloat64(1, view.getFloat64(1, true), true);
        if (this.dataChannel?.readyState === 'open') this.dataChannel.send(pong);
        return;
      }
      if (tag === DC_PONG) {
        const sent = view.getFloat64(1, true);
        this.p2pLatency = Math.max(0, Math.round(performance.now() - sent));
        if (this.onP2PRtt) this.onP2PRtt(this.p2pLatency);
        return;
      }
    }

    // Input message (legacy digital or extended analog format)
    if (!this.onRemoteInput) return;
    const decoded = decodeInputFrame(data);
    if (decoded) {
      this.onRemoteInput(
        decoded.bitmask,
        decoded.targetFrame,
        decoded.analogX,
        decoded.analogY,
      );
    }
  }

  _startDCPingLoop() {
    this._stopDCPingLoop();
    this._dcPingInterval = setInterval(() => {
      if (this.dataChannel?.readyState !== 'open') return;
      const buf = new ArrayBuffer(DC_PING_SIZE);
      const view = new DataView(buf);
      view.setUint8(0, DC_PING);
      view.setFloat64(1, performance.now(), true);
      this.dataChannel.send(buf);
    }, DC_PING_INTERVAL_MS);
  }

  _stopDCPingLoop() {
    if (this._dcPingInterval) {
      clearInterval(this._dcPingInterval);
      this._dcPingInterval = null;
    }
  }

  // ── Connection Monitoring ──────────────────────────────

  _setupConnectionMonitoring() {
    this.pc.onconnectionstatechange = () => {
      const state = this.pc.connectionState;
      if (this.onConnectionStateChange) this.onConnectionStateChange(state);
      if (state === 'failed' || state === 'disconnected') {
        if (this.onError) this.onError('WebRTC connection lost');
      }
    };

    this.pc.oniceconnectionstatechange = () => {
      if (this.pc.iceConnectionState === 'failed') {
        if (this.onError) this.onError('P2P connection failed. Both players may be behind strict NATs');
      }
    };
  }

  // ── Cleanup ────────────────────────────────────────────

  destroy() {
    this._resetMicrophone();
    this._stopDCPingLoop();
    this._cleanupAudioCapture();

    if (this._micAnalyserCtx) {
      this._micAnalyserCtx.close().catch(() => {});
      this._micAnalyserCtx = null;
    }
    this._micAnalyser = null;
    this._micAnalyserData = null;

    // Clean up remote voice audio
    if (this._voiceAudio) {
      this._voiceAudio.srcObject = null;
      this._voiceAudio = null;
    }
    if (this._remoteAnalyserCtx) {
      this._remoteAnalyserCtx.close().catch(() => {});
      this._remoteAnalyserCtx = null;
      this._remoteAnalyser = null;
      this._remoteAnalyserData = null;
    }
    this._gameStream = null;

    this._teardownGuestAudio();

    if (this.dataChannel) {
      this.dataChannel.close();
      this.dataChannel = null;
    }

    if (this.pc) {
      this.pc.close();
      this.pc = null;
    }

    if (this.audioStreamDest) {
      this.audioStreamDest = null;
    }
    this._muteGain = null;

    document.getElementById('guest-unmute-overlay')?.remove();

    // Restore previous signal handler
    this.network.onSignal = this._prevOnSignal || null;
  }
}
